import { memo } from 'react';
import { MessageCircleIcon, MousePointerClickIcon, UsersIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { StrategicElementsGridProps } from './StrategicElementsGrid.types';
import { getWidgetStores } from '@/entities/widget';
import { MotionBox } from '@/shared/ui/motion';
import { useTranslation } from 'react-i18next';

/**
 * Icon mapping for strategic elements. The keys are the raw `title` values the
 * agent emits, so they are payload tokens rather than display copy.
 */
const ELEMENT_ICON_MAP: Record<string, LucideIcon> = {
  'Core Tension': MousePointerClickIcon,
  'Key Message': MessageCircleIcon,
  Audience: UsersIcon,
};

/**
 * StrategicElementsGrid displays key strategic elements in a responsive grid layout
 *
 * Features:
 * - Responsive layout (row in fullscreen, 2-column grid in panel mode)
 * - Accessible with proper ARIA labels and semantic structure
 * - Smooth layout animations via motion
 * - Memoized to prevent unnecessary re-renders
 */
export const StrategicElementsGrid = memo(function StrategicElementsGrid({
  elements,
}: StrategicElementsGridProps) {
  const { t } = useTranslation('contentTest');

  const { sidePanelLayoutMode } = getWidgetStores().uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  if (elements.length === 0) {
    return null;
  }

  return (
    <section
      aria-labelledby="strategic-elements-title"
      className="flex w-full flex-col items-center gap-12 px-0 py-20"
    >
      {/* Section Title */}
      <div className="flex w-full flex-col items-center justify-center pb-6 pl-15">
        <h2 id="strategic-elements-title" className="w-full text-display-sm text-foreground">
          {t('ideationWidget.strategicElementsGrid.keyStrategicElements')}
        </h2>
      </div>

      {/* Cards Container */}
      <MotionBox
        layout="preserve-aspect"
        className="grid w-full gap-4 px-21"
        style={{
          gridTemplateColumns: isFullscreen ? `repeat(${elements.length}, 1fr)` : 'repeat(2, 1fr)',
        }}
        role="list"
        aria-label={t('ideationWidget.strategicElementsGrid.strategicElementsList')}
      >
        {elements.map((element, index) => {
          // Falls back to the tension icon when the agent emits a title the map
          // does not cover.
          const Icon = ELEMENT_ICON_MAP[element.title] ?? MousePointerClickIcon;

          return (
            <div
              key={`element-${index}`}
              role="listitem"
              className="flex flex-col overflow-hidden rounded-3xl bg-surface p-10"
            >
              <div className="flex flex-col gap-8">
                {/* Icon and Title */}
                <div className="flex flex-row items-start justify-start gap-2">
                  <Icon className="size-8 shrink-0 text-primary" aria-hidden="true" />
                  <h3 className="text-heading-xl text-primary">{element.title}</h3>
                </div>

                <p className="text-xl text-foreground">{element.content}</p>
              </div>
            </div>
          );
        })}
      </MotionBox>
    </section>
  );
});
